import Spinner from '@xo-union/tk-component-spinner';
import React, { useEffect } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import { useWeddingWebsiteQueryLazyQuery } from '../generated/graphql';
import ThemePicker from './ThemePicker';
import WeddingDetails from './WeddingDetails';

import { ThemeFiltersVariantProvider } from './helpers/ThemeFiltersContext';
import styles from './App.scss';
import { TrackingProvider } from './helpers/TrackingContext';
import { FeatureToggleEnum, useFeatureTogglesContext } from '../context/FeatureTogglesContext';
import { OnboardingContext, OnboardingProvider } from './OnboardingContext';
import ChooseOnboardingType from './ChooseOnboardingType';
import { useSetLastVisitTimestamp } from '../hooks/useSetLastVisitTimestamp';
import { PaginatedThemePicker } from './PaginatedThemes/PaginatedThemePicker';
import RouteMeta from './RouteMeta';
import { changeThemeRoute, onboardingDesignPLPRoute } from '../helpers/routes';
import { withCurrentQueryParams } from '../helpers/withCurrentQueryParams';

const App: React.FC = () => {
  const location = useLocation();

  const onCompleted = () => {
    //to allow development and not be redirected
    if (process.env.NODE_ENV === 'development') return;

    if (wwsData?.weddingWebsite.state === 'published' || wwsData?.weddingWebsite.state === 'unpublished') {
      // keep inbound marketing params (utm_*, gclid, ...) across the redirect
      window.location.replace(withCurrentQueryParams(changeThemeRoute, location.search));
    }
  };

  const [callWWSQuery, { data: wwsData, loading }] = useWeddingWebsiteQueryLazyQuery({ onCompleted });
  useSetLastVisitTimestamp();

  useEffect(() => {
    callWWSQuery();
  }, [callWWSQuery]);

  const { onboardingType } = React.useContext(OnboardingContext);

  const { isEnabled } = useFeatureTogglesContext();
  const isAiOnboardingEnabled = isEnabled(FeatureToggleEnum.AiOnboarding);
  const isPaginatedThemeGridEnabled = isEnabled(FeatureToggleEnum.PaginatedThemeGrid);

  const controlRouting = () => {
    return (
      <Switch>
        <Route exact path={'/gs/wedding-websites/designs/:id'}>
          <OnboardingProvider>
            <WeddingDetails />
          </OnboardingProvider>
        </Route>
        <Route path={['/gs/designs', '/gs/wedding-websites', '/gs/wedding-websites/designs']}>
          {isPaginatedThemeGridEnabled ? <PaginatedThemePicker /> : <ThemePicker />}
        </Route>
      </Switch>
    );
  };

  const variantRouting = () => {
    return (
      <Switch>
        <Route exact path={'/gs/wedding-websites/designs/:id'}>
          <ChooseOnboardingType />
        </Route>
        <Route path={['/gs/designs', '/gs/wedding-websites', '/gs/wedding-websites/designs']}>
          <ChooseOnboardingType />
        </Route>
      </Switch>
    );
  };

  const getRoutingByUserChoice = () => {
    if (onboardingType === 'AI') {
      return variantRouting();
    } else {
      return controlRouting();
    }
  };

  return (
    <>
      <RouteMeta
        title="Wedding Website Templates & Designs - The Knot"
        description="Explore 100+ free wedding website templates from The Knot. Choose a beautiful design,
 customize it to match your style, and share it with guests."
        robots="index,nofollow"
        matchPath={onboardingDesignPLPRoute}
      />
      {loading ? (
        <div className={styles.spinnerHolder}>
          <Spinner />
        </div>
      ) : (
        <ThemeFiltersVariantProvider>
          <TrackingProvider>
            {isAiOnboardingEnabled && !onboardingType ? <ChooseOnboardingType /> : getRoutingByUserChoice()}
          </TrackingProvider>
        </ThemeFiltersVariantProvider>
      )}
    </>
  );
};

export default App;
